import React, { useEffect, useState } from 'react';
import {
    FaTimes,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaSearch,
    FaListUl,
    FaTruck,
    FaChevronDown,
    FaBox,
    FaRupeeSign,
    FaUser,
    FaClock,
    FaSpinner,
    FaCheckCircle,
    FaFileDownload,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { useMyContext } from '../../../context/MyContext';
import { Base_Url, shipmentallList, track, Url_For_View_Document } from '../../../config/config';

// =========================
// Status Color
// =========================
const statusColor = (status) => {
    const s = (status || '').toLowerCase();

    if (s.includes('deliver')) return 'bg-emerald-500';
    if (s.includes('transit')) return 'bg-blue-500';
    if (s.includes('out for delivery')) return 'bg-orange-500';

    if (
        s.includes('fail') ||
        s.includes('exception') ||
        s.includes('cancel')
    ) {
        return 'bg-red-500';
    }

    return 'bg-gray-400';
};

// =========================
// Robust Scans Extractor
// Priority #1: `detail.Scans` — this is the EXACT same field
// that OrderTracking.jsx (the working "Track Your Shipment" page)
// uses directly via `orderTrackingInfo.Scans?.map(...)`.
// So whatever works on that page now works here too, with
// zero transformation (no risk of the normalizer breaking shape).
// =========================
const extractScans = (detail) => {
    if (!detail) return [];

    // ✅ Same source of truth as OrderTracking.jsx
    if (Array.isArray(detail?.Scans) && detail.Scans.length > 0) {
        return detail.Scans;
    }

    // Some backend responses (rare) wrap it under slightly
    // different casing/key. Try those next.
    const possibleArrays = [
        detail?.Scan,
        detail?.ScanDetail,
        detail?.scans,
        detail?.scan,
        detail?.TrackingHistory,
        detail?.tracking_history,
        detail?.Status?.Scans,
    ];

    for (const arr of possibleArrays) {
        if (Array.isArray(arr) && arr.length > 0) {
            // normalize: make sure every item has a `.ScanDetail` shape
            return arr.map((item) => {
                if (item?.ScanDetail) return item;
                return {
                    ScanDetail: {
                        Instructions:
                            item?.Instructions ||
                            item?.Remark ||
                            item?.Status ||
                            'Shipment update',
                        StatusDateTime:
                            item?.StatusDateTime ||
                            item?.DateTime ||
                            item?.date ||
                            item?.created_at,
                        ScannedLocation:
                            item?.ScannedLocation ||
                            item?.Location ||
                            item?.location,
                        Scan: item?.Scan || item?.ScanType,
                    },
                };
            });
        }
    }

    // 🔍 Debug: reaches here only when NONE of the known keys had
    // a scans array. Logs the actual keys present on `detail` so
    // you can see exactly what shape THIS order returned.
    console.warn(
        '[TrackingSidePopup] No scans array found. Available keys on detail:',
        Object.keys(detail || {}),
        'Status object:',
        detail?.Status
    );

    // Fallback: build a single-step timeline from Status
    // so the flow isn't completely empty when the API
    // doesn't return a scans array (e.g. some delivered orders).
    if (detail?.Status?.Status) {
        return [
            {
                ScanDetail: {
                    Instructions:
                        detail?.Status?.Instructions ||
                        detail?.Status?.Status,
                    StatusDateTime: detail?.Status?.StatusDateTime,
                    ScannedLocation:
                        detail?.Destination || detail?.Origin || '',
                    Scan: detail?.Status?.Status,
                },
            },
        ];
    }

    return [];
};

// =========================
// Skeleton Card
// =========================
const SkeletonCard = ({ isDarkMode }) => (
    <div
        className={`
      rounded-[20px]
      border
      p-4
      animate-pulse
      backdrop-blur-2xl
      shadow-[0_8px_30px_rgba(0,0,0,0.04)]
      ${isDarkMode
                ? 'bg-white/[0.08] border-white/10'
                : 'bg-white/50 border-white/70'
            }
    `}
    >
        <div className="flex items-center justify-between mb-3">
            <div
                className={`h-4 w-28 rounded-full ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                    }`}
            />

            <div
                className={`h-5 w-16 rounded-full ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                    }`}
            />
        </div>

        <div
            className={`h-3 w-32 rounded-full mb-2 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                }`}
        />

        <div
            className={`h-3 w-20 rounded-full mb-4 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                }`}
        />

        <div
            className={`h-9 w-full rounded-xl ${isDarkMode ? 'bg-white/10' : 'bg-black/10'
                }`}
        />
    </div>
);

// =========================
// Accordion Detail Row
// =========================
const DetailRow = ({ label, value, icon: Icon, isDarkMode }) => (
    <div
        className={`
      flex items-center justify-between
      py-2.5
      border-b last:border-b-0
      ${isDarkMode ? 'border-white/10' : 'border-black/[0.06]'}
    `}
    >
        <div className="flex items-center gap-2">
            {Icon && (
                <div
                    className={`
            w-6 h-6 rounded-lg
            flex items-center justify-center flex-shrink-0
            ${isDarkMode ? 'bg-white/10' : 'bg-black/[0.05]'}
          `}
                >
                    <Icon
                        className={`w-3 h-3 ${isDarkMode ? 'text-white/40' : 'text-black/40'
                            }`}
                    />
                </div>
            )}
            <span
                className={`text-[10.5px] font-medium ${isDarkMode ? 'text-white/50' : 'text-black/50'
                    }`}
            >
                {label}
            </span>
        </div>
        <span
            className={`text-[11px] font-semibold text-right ${isDarkMode ? 'text-white' : 'text-black'
                }`}
        >
            {value || 'Not Available'}
        </span>
    </div>
);

// =========================
// Accordion Section
// =========================
const AccordionSection = ({
    icon: Icon,
    title,
    isOpen,
    onToggle,
    isDarkMode,
    children,
}) => (
    <div
        className={`
      rounded-[16px]
      border
      overflow-hidden
      transition-all
      duration-300
      ${isDarkMode
                ? 'bg-white/[0.05] border-white/10'
                : 'bg-white border-black/[0.06]'
            }
    `}
    >
        <button
            onClick={onToggle}
            className={`
        w-full
        flex items-center justify-between
        px-3.5 py-3
      `}
        >
            <div className="flex items-center gap-2">
                <div
                    className={`
            w-6 h-6 rounded-lg
            flex items-center justify-center flex-shrink-0
            ${isDarkMode ? 'bg-white/10' : 'bg-black/[0.06]'}
          `}
                >
                    <Icon
                        className={`w-3 h-3 ${isDarkMode ? 'text-white' : 'text-black'
                            }`}
                    />
                </div>
                <span
                    className={`text-[11.5px] font-bold ${isDarkMode ? 'text-white' : 'text-black'
                        }`}
                >
                    {title}
                </span>
            </div>

            <FaChevronDown
                size={10}
                className={`
          transition-transform duration-300
          ${isOpen ? 'rotate-180' : 'rotate-0'}
          ${isDarkMode ? 'text-white/40' : 'text-black/35'}
        `}
            />
        </button>

        <div
            className={`
        grid transition-all duration-300 ease-in-out
        ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
      `}
        >
            <div className="overflow-hidden">
                <div className="px-3.5 pb-3">{children}</div>
            </div>
        </div>
    </div>
);

// =========================
// Tracking Side Popup
// =========================
// ⚡ Cache helpers (same as AllShipment.js) to show popup instantly
const SHIPMENT_CACHE_PREFIX = 'b2c_all_shipments_cache_';
const readShipmentCache = (cusId) => {
    try {
        const raw = localStorage.getItem(SHIPMENT_CACHE_PREFIX + (cusId || ''));
        const parsed = raw ? JSON.parse(raw) : null;
        if (Array.isArray(parsed)) return parsed;
    } catch (e) { /* ignore */ }
    return [];
};

const TrackingSidePopup = ({
    isOpen,
    onClose,
    searchTerm,
    setSearchTerm,
    isDarkMode,
}) => {
    const [shipments, setShipments] = useState(() => readShipmentCache(localStorage.getItem('cus_id')));
    const [loading, setLoading] = useState(false);

    // which order card is expanded
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailError, setDetailError] = useState('');

    // which accordion section is open inside the expanded card
    const [openSection, setOpenSection] = useState('overview');

    // direct-search (fallback when typed ID isn't in the
    // pre-fetched list) — mirrors what OrderTracking.jsx does
    const [directSearchLoading, setDirectSearchLoading] = useState(false);
    const [directSearchResult, setDirectSearchResult] = useState(null);
    const [directSearchError, setDirectSearchError] = useState('');

    // Status hover tooltip (shows FULL status text on hover,
    // e.g. "Delivered to consignee - Code Verified delivery")
    const [statusHover, setStatusHover] = useState({ id: null, label: '', x: 0, y: 0, above: false });

    const { setorderId } = useMyContext();
    const navigate = useNavigate();

    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    // =========================
    // Fetch Shipments
    // =========================
    useEffect(() => {
        if (isOpen) {
            fetchShipments();
        }

        if (!isOpen) {
            setExpandedOrderId(null);
            setDetail(null);
            setDetailError('');
            setDirectSearchResult(null);
            setDirectSearchError('');
        }
    }, [isOpen]);

    const fetchShipments = async () => {
        // Agar cache me data hai to loading skeleton mat dikhao
        if (shipments.length === 0) setLoading(true);

        try {
            const res = await fetch(Base_Url + shipmentallList, {
                method: 'GET',
                headers: {
                    token,
                    cus_id,
                    'Content-Type': 'application/json',
                },
            });

            const data = await res.json();

            // ✅ Fetch the FULL list — same as AllShipment.jsx (no slicing)
            // so every field (including epod_file / full status) is
            // consistent between both screens.
            const freshData = Array.isArray(data?.data) ? data.data : [];
            setShipments(freshData);
            try { localStorage.setItem(SHIPMENT_CACHE_PREFIX + (cus_id || ''), JSON.stringify(freshData)); } catch(e) {}
        } catch (err) {
            console.error('Error fetching shipments:', err);
            setShipments([]);
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // Search (local, against the FULL pre-fetched shipments list)
    // =========================
    const filtered = shipments.filter((s) => {
        if (!searchTerm?.trim()) return true;

        const term = searchTerm.toLowerCase();

        return (
            s?.order_id?.toString().toLowerCase().includes(term) ||
            s?.waybill?.toString().toLowerCase().includes(term)
        );
    });

    // =========================
    // Direct search fallback
    // Runs ONLY when the local list has zero matches for what
    // the user typed. Calls the same `track` API that
    // OrderTracking.jsx uses, with the typed value as order_id —
    // so any valid order ID works here too, not just the ones in
    // `shipmentallList`.
    // =========================
    useEffect(() => {
        const term = searchTerm?.trim();

        setDirectSearchResult(null);
        setDirectSearchError('');

        if (!isOpen || !term || filtered.length > 0) return;

        const timer = setTimeout(async () => {
            setDirectSearchLoading(true);
            try {
                const res = await axios.post(
                    Base_Url + track,
                    { order_id: term, shipment_type: 'Forward' },
                    {
                        headers: {
                            token,
                            cus_id,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                const shipment = res?.data?.data?.ShipmentData?.[0]?.Shipment;

                if (res?.data?.status === 'SUCCESS' && shipment) {
                    setDirectSearchResult(shipment);
                } else {
                    setDirectSearchError(
                        res?.data?.message || 'No shipment found for this ID'
                    );
                }
            } catch (err) {
                console.error('Direct search tracking error:', err);
                setDirectSearchError('Failed to fetch tracking information');
            } finally {
                setDirectSearchLoading(false);
            }
        }, 500); // debounce so it doesn't fire on every keystroke

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, filtered.length, isOpen]);

    // Turn the direct-search result into a normal-shaped shipment
    // row so it can reuse all the existing card + accordion JSX
    // below, with zero duplicated markup.
    const syntheticShipment = directSearchResult
        ? {
            order_id: searchTerm.trim(),
            waybill: directSearchResult?.AWB || searchTerm.trim(),
            status: directSearchResult?.Status?.Status,
            pickup_city: directSearchResult?.Origin,
            city: directSearchResult?.Destination,
            created_at: directSearchResult?.PickUpDate,
            epod_file: directSearchResult?.epod_file,
        }
        : null;

    const displayList = syntheticShipment
        ? [syntheticShipment, ...filtered]
        : filtered;

    // =========================
    // Toggle View Details (accordion under the card)
    // =========================
    const handleToggleDetails = async (orderId) => {
        // already expanded -> collapse
        if (expandedOrderId === orderId) {
            setExpandedOrderId(null);
            setDetail(null);
            setDetailError('');
            return;
        }

        // If this row is the synthetic direct-search result, we
        // already have its detail in `directSearchResult` — reuse
        // it instead of firing another network call.
        if (syntheticShipment && orderId === syntheticShipment.order_id) {
            setorderId(orderId);
            setExpandedOrderId(orderId);
            setDetail(directSearchResult);
            setDetailError('');
            setOpenSection('status');
            return;
        }

        setorderId(orderId);
        setExpandedOrderId(orderId);
        setDetail(null);
        setDetailError('');
        setOpenSection('overview');
        setDetailLoading(true);

        try {
            const res = await axios.post(
                Base_Url + track,
                {
                    order_id: orderId,
                    shipment_type: 'Forward',
                },
                {
                    headers: {
                        token,
                        cus_id,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const shipment = res?.data?.data?.ShipmentData?.[0]?.Shipment;

            if (res?.data?.status === 'SUCCESS' && shipment) {
                setDetail(shipment);
            } else {
                setDetailError(
                    res?.data?.message || 'Unable to fetch tracking details'
                );
            }
        } catch (err) {
            console.error('Tracking error:', err);
            setDetailError('Failed to fetch tracking information');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleToggleSection = (key) => {
        setOpenSection((prev) => (prev === key ? null : key));
    };

    // =========================
    // Download POD
    // Always uses the LIST item (`s` from `shipmentallList`),
    // never `detail` (which comes from the `track` courier API
    // and never has `epod_file`).
    // =========================
    const handleDownloadPOD = (row) => {
        if (!row?.epod_file) {
            toast.error('POD not available for this order yet');
            return;
        }
        window.open(Url_For_View_Document + row.epod_file, '_blank');
        toast.success('POD downloaded successfully!');
    };

    // =========================
    // Status hover handlers — shows FULL status text
    // (e.g. "Delivered to consignee - Code Verified delivery")
    // in a dark tooltip, same style as AllShipment.jsx's
    // StatusTooltip.
    // =========================
    const handleStatusEnter = (e, rowId, label) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const above = spaceBelow < 60;
        setStatusHover({
            id: rowId,
            label,
            x: rect.left + rect.width / 2,
            y: above ? rect.top + window.scrollY : rect.bottom + window.scrollY,
            above,
        });
    };

    const handleStatusLeave = () =>
        setStatusHover({ id: null, label: '', x: 0, y: 0, above: false });

    // Status Tooltip Portal
    const StatusTooltip = () => {
        if (!statusHover.id) return null;
        const style = {
            position: 'fixed',
            left: statusHover.x,
            top: statusHover.above ? 'auto' : statusHover.y - window.scrollY,
            bottom: statusHover.above ? window.innerHeight - (statusHover.y - window.scrollY) : 'auto',
            zIndex: 999999,
            pointerEvents: 'none',
            transform: 'translateX(-50%)',
        };
        return createPortal(
            <div style={style} className="flex flex-col items-center max-w-[260px]">
                {!statusHover.above && <div className="w-2.5 h-2.5 bg-gray-900 rotate-45 -mb-1 flex-shrink-0"></div>}
                <div className="bg-gray-900 text-white text-[10.5px] font-medium px-3 py-1.5 rounded-lg shadow-xl whitespace-normal text-center leading-snug">
                    {statusHover.label}
                </div>
                {statusHover.above && <div className="w-2.5 h-2.5 bg-gray-900 rotate-45 -mt-1 flex-shrink-0"></div>}
            </div>,
            document.body
        );
    };

    // =========================
    // View All
    // =========================
    const handleViewAll = () => {
        onClose();
        navigate('/shipments/all');
    };

    // =========================
    // Date
    // =========================
    const formatDate = (date) => {
        if (!date) return 'Not Available';

        try {
            return new Date(date).toLocaleString('en-IN');
        } catch {
            return 'Not Available';
        }
    };

    // =========================
    // Don't render when closed
    // =========================
    if (!isOpen) return null;

    // scans for the currently expanded shipment
    const scans = extractScans(detail);

    return (
        <>
            {/* =====================================
          CUSTOM ANIMATION
      ====================================== */}
            <style>
                {`
          @keyframes trackingPopupIn {
            0% {
              transform: translateX(110%);
              opacity: 0;
            }

            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }

          .tracking-popup-enter {
            animation: trackingPopupIn
              0.5s
              cubic-bezier(0.22, 1, 0.36, 1)
              forwards;
          }

          .tracking-popup-scroll::-webkit-scrollbar {
            width: 4px;
          }

          .tracking-popup-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          .tracking-popup-scroll::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.12);
            border-radius: 999px;
          }

          .tracking-popup-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.22);
          }

          .tracking-popup-scroll {
            scrollbar-width: thin;
            scrollbar-color: rgba(0,0,0,0.12) transparent;
          }
        `}
            </style>

            {/* =====================================
          BACKDROP
      ====================================== */}
            <div
                className="
          fixed
          inset-0
          z-[9998]
          bg-black/20
          backdrop-blur-[6px]
          animate-[fadeIn_0.3s_ease-out]
        "
                onClick={onClose}
            />

            {/* =====================================
          APPLE GLASS PANEL
      ====================================== */}
            <div
                className={`
          tracking-popup-enter

          fixed
          top-3
          right-3
          bottom-3

          w-[min(350px,70vw)]

          z-[9999]
          overflow-hidden

          rounded-[28px]

          border
          border-white/70

          shadow-[0_25px_80px_rgba(0,0,0,0.18)]

          backdrop-blur-[45px]

          flex
          flex-col

          ${isDarkMode
                        ? `
                bg-slate-900/80
                text-white
              `
                        : `
                bg-gradient-to-br
                from-white/80
                via-slate-100/70
                to-blue-100/60
                text-black
              `
                    }
        `}
            >
                {/* =====================================
            GLASS TINT BLOBS
        ====================================== */}

                <div
                    className="
            pointer-events-none
            absolute
            -top-24
            -right-20
            w-56
            h-56
            rounded-full
            bg-blue-300/20
            blur-[70px]
          "
                />

                <div
                    className="
            pointer-events-none
            absolute
            top-[35%]
            -left-24
            w-52
            h-52
            rounded-full
            bg-purple-300/15
            blur-[70px]
          "
                />

                <div
                    className="
            pointer-events-none
            absolute
            bottom-[-80px]
            right-[-50px]
            w-64
            h-64
            rounded-full
            bg-cyan-300/15
            blur-[80px]
          "
                />

                {/* =====================================
            HEADER
        ====================================== */}
                <div
                    className={`
            relative
            z-10

            flex
            items-center
            justify-between

            px-5
            py-4

            border-b

            ${isDarkMode ? 'border-white/10' : 'border-black/[0.06]'}
          `}
                >
                    <div className="flex items-center gap-3">
                        <div
                            className={`
                w-9
                h-9
                rounded-[13px]

                flex
                items-center
                justify-center

                border
                shadow-sm

                ${isDarkMode
                                    ? 'bg-white/10 border-white/10'
                                    : 'bg-white/65 border-white/80'
                                }
              `}
                        >
                            <FaTruck
                                size={15}
                                className={isDarkMode ? 'text-white' : 'text-black'}
                            />
                        </div>

                        <p
                            className={`
                text-[16px]
                font-semibold

                ${isDarkMode ? 'text-white/60' : 'text-black/55'}
              `}
                        >
                            Track your shipments
                        </p>
                    </div>

                    {/* Close */}
                    <button
                        onClick={onClose}
                        className={`
              w-8
              h-8

              flex
              items-center
              justify-center

              rounded-full
              border

              transition-all
              duration-200

              ${isDarkMode
                                ? `
                    bg-white/10
                    border-white/10
                    text-white
                    hover:bg-white/20
                  `
                                : `
                    bg-white/60
                    border-white/80
                    text-black
                    hover:bg-white/90
                  `
                            }
            `}
                    >
                        <FaTimes size={13} />
                    </button>
                </div>

                {/* Search */}
                <div className="relative z-10 px-5 py-3.5">
                    <div className="relative">
                        <FaSearch color='blue'
                            className={`
                absolute
                left-3.5
                top-1/2
                -translate-y-1/2

                ${isDarkMode ? 'text-white/40' : ''}
              `}
                            size={12}
                        />

                        <input
                            autoFocus
                            type="text"
                            value={searchTerm || ''}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by Tracking ID..."
                            className={`
                w-full

                pl-9
                pr-4
                py-2.5

                rounded-[14px]

                text-[12px]
                font-medium

                outline-none
                border

                backdrop-blur-xl

                transition-all
                duration-200

                ${isDarkMode
                                    ? `
                      bg-white/10
                      border-white/10
                      text-white
                      placeholder:text-white/35
                      focus:bg-white/15
                    `
                                    : `
                      bg-white/60
                      border-white/80
                      text-black
                      placeholder:text-black/35
                      focus:bg-white/80
                    `
                                }
              `}
                        />
                    </div>
                </div>

                {/* =====================================
            SHIPMENT LIST (with inline accordion detail)
        ====================================== */}
                <div
                    className="
            relative
            z-10
            flex-1

            overflow-y-auto
            tracking-popup-scroll

            px-5
            pb-4

            space-y-2.5
          "
                >
                    {/* Loading (initial list fetch) */}
                    {loading && (
                        <>
                            <SkeletonCard isDarkMode={isDarkMode} />
                            <SkeletonCard isDarkMode={isDarkMode} />
                            <SkeletonCard isDarkMode={isDarkMode} />
                        </>
                    )}

                    {/* Loading (direct search fallback) */}
                    {!loading && directSearchLoading && (
                        <SkeletonCard isDarkMode={isDarkMode} />
                    )}

                    {/* Empty (nothing local, nothing from direct search) */}
                    {!loading &&
                        !directSearchLoading &&
                        displayList.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div
                                    className={`
                  w-12
                  h-12
                  rounded-full

                  flex
                  items-center
                  justify-center

                  mb-3
                  border

                  ${isDarkMode
                                            ? 'bg-white/10 border-white/10'
                                            : 'bg-white/60 border-white/80'
                                        }
                `}
                                >
                                    <FaSearch
                                        size={14}
                                        className={
                                            isDarkMode ? 'text-white/40' : 'text-black/30'
                                        }
                                    />
                                </div>

                                <p
                                    className={`
                  text-[11px]
                  font-semibold

                  ${isDarkMode ? 'text-white/60' : 'text-black/55'}
                `}
                                >
                                    {searchTerm?.trim()
                                        ? directSearchError || 'No shipments found'
                                        : 'No shipments found'}
                                </p>
                            </div>
                        )}

                    {/* Shipment Cards (local matches + direct-search result) */}
                    {!loading &&
                        !directSearchLoading &&
                        displayList.map((s, index) => {
                            const orderId = s?.order_id;
                            const isExpanded = expandedOrderId === orderId;
                            const isDelivered = s?.status?.toLowerCase().includes('delivered');
                            const fullStatus = s?.status || 'Pending';

                            return (
                                <div key={orderId || s?.waybill || index}>
                                    {/* ===== CARD ===== */}
                                    <div
                                        className={`
                      group

                      rounded-[19px]

                      p-3.5

                      border

                      backdrop-blur-2xl

                      transition-all
                      duration-300

                      shadow-[0_8px_30px_rgba(0,0,0,0.04)]

                      ${isDarkMode
                                                ? `
                            bg-white/[0.08]
                            border-white/10
                            hover:bg-white/[0.12]
                          `
                                                : `
                            bg-white/55
                            border-white/80
                            hover:bg-white/75
                          `
                                            }
                    `}
                                    >
                                        {/* Top */}
                                        <div className="flex items-center gap-0.5 justify-between mb-2">
                                            <span
                                                className={`
                          font-bold
                          text-[12px]
                          tracking-[-0.01em]

                          ${isDarkMode ? 'text-white' : 'text-black'}
                        `}
                                            >
                                                {s?.waybill || s?.order_id || 'N/A'}
                                            </span>

                                            {isDelivered && s?.epod_file && (
                                                <button
                                                    onClick={() => handleDownloadPOD(s)}
                                                    title="Download POD"
                                                    className={`
                          flex-shrink-0
                          ml-7
                          flex
                          items-center
                          justify-center
                          gap-1.5

                          py-1
                          px-1

                          rounded-[13px]

                          text-[9px]
                          font-bold

                          border

                          transition-all
                          duration-200

                          ${isDarkMode
                                                            ? `
                                bg-blue-500/20
                                border-blue-400/20
                                text-blue-300
                                hover:bg-blue-500/30
                              `
                                                            : `
                                bg-blue-900
                                border-blue-100
                                text-blue-600
                                hover:bg-blue-600
                              `
                                                        }
                        `}
                                                >
                                                    <FaFileDownload color={isDarkMode ? "black" : "white"} size={12} />

                                                </button>
                                            )}

                                            {/* Status badge — hover shows FULL status text in a tooltip */}
                                            <span
                                                onMouseEnter={(e) => handleStatusEnter(e, orderId, fullStatus)}
                                                onMouseLeave={handleStatusLeave}
                                                className={`
                          text-[8px]

                          px-2
                          py-1

                          rounded-full

                          text-white
                          font-bold
                          tracking-wide

                          shadow-sm

                          cursor-default

                          max-w-[110px]
                          truncate

                          ${statusColor(s?.status)}
                        `}
                                            >
                                                {fullStatus}
                                            </span>
                                        </div>

                                        {/* Route */}
                                        <div
                                            className={`
                        flex
                        items-center
                        gap-2

                        text-[10px]
                        font-medium

                        mb-1.5

                        ${isDarkMode ? 'text-white/65' : 'text-black/60'}
                      `}
                                        >
                                            <FaMapMarkerAlt
                                                size={9}
                                                className={
                                                    isDarkMode ? 'text-white/40' : 'text-black/35'
                                                }
                                            />

                                            <span>
                                                {s?.pickup_city || '—'} → {s?.city || '—'}
                                            </span>
                                        </div>

                                        {/* Date */}
                                        <div
                                            className={`
                        flex
                        items-center
                        gap-2

                        text-[9px]
                        font-medium

                        mb-3

                        ${isDarkMode ? 'text-white/40' : 'text-black/40'}
                      `}
                                        >
                                            <FaCalendarAlt size={9} />

                                            <span>
                                                {s?.created_at
                                                    ? new Date(s.created_at).toLocaleDateString(
                                                        'en-IN'
                                                    )
                                                    : '—'}
                                            </span>
                                        </div>

                                        {/* Action buttons row: View Details + (Download POD if delivered) */}
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleToggleDetails(orderId)}
                                                className={`
                        flex-1

                        flex
                        items-center
                        justify-center
                        gap-1.5

                        py-2.5

                        rounded-[13px]

                        text-[10px]
                        font-bold

                        border

                        transition-all
                        duration-200

                        ${isDarkMode
                                                        ? `
                              bg-white/10
                              border-white/10
                              text-white
                              hover:bg-white/20
                            `
                                                        : `
                              bg-black/[0.04]
                              border-black/[0.06]
                              text-black
                              hover:bg-black/[0.08]
                            `
                                                    }
                      `}
                                            >
                                                {isExpanded && detailLoading ? (
                                                    <FaSpinner size={10} className="animate-spin" />
                                                ) : (
                                                    <FaChevronDown
                                                        size={9}
                                                        className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''
                                                            }`}
                                                    />
                                                )}
                                                {isExpanded ? 'Hide Details' : 'View Details'}
                                            </button>

                                            {/* Download POD — only when delivered AND epod_file exists */}

                                        </div>
                                    </div>

                                    {/* ===== INLINE ACCORDION DETAIL (below the card) ===== */}
                                    {isExpanded && (
                                        <div className="mt-2.5 space-y-2.5 animate-[fadeIn_0.25s_ease-out]">
                                            {/* Loading */}
                                            {detailLoading && (
                                                <SkeletonCard isDarkMode={isDarkMode} />
                                            )}

                                            {/* Error */}
                                            {!detailLoading && detailError && (
                                                <div
                                                    className={`
                            p-3
                            rounded-[16px]
                            border
                            text-center
                            text-[10.5px]
                            font-semibold
                            text-red-500
                            ${isDarkMode
                                                            ? 'bg-red-500/10 border-red-500/20'
                                                            : 'bg-red-50 border-red-100'
                                                        }
                          `}
                                                >
                                                    {detailError}
                                                </div>
                                            )}

                                            {/* Detail Accordion Sections */}
                                            {!detailLoading && detail && (
                                                <>
                                                    {/* Current Status — Accordion with full flow */}
                                                    <AccordionSection
                                                        icon={FaClock}
                                                        title="Current Status"
                                                        isOpen={openSection === 'status'}
                                                        onToggle={() =>
                                                            handleToggleSection('status')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        {/* Top summary */}
                                                        <div className="mb-3">
                                                            <p
                                                                className={`text-[11px] font-bold mb-0.5 ${isDarkMode
                                                                    ? 'text-white'
                                                                    : 'text-black'
                                                                    }`}
                                                            >
                                                                {detail?.Status?.Status ||
                                                                    'Pending'}
                                                            </p>
                                                            <p
                                                                className={`text-[9.5px] leading-relaxed ${isDarkMode
                                                                    ? 'text-white/50'
                                                                    : 'text-black/50'
                                                                    }`}
                                                            >
                                                                {detail?.Status?.Instructions ||
                                                                    'No instructions available'}
                                                            </p>
                                                            <p
                                                                className={`text-[8px] mt-1 ${isDarkMode
                                                                    ? 'text-white/30'
                                                                    : 'text-black/30'
                                                                    }`}
                                                            >
                                                                Updated:{' '}
                                                                {formatDate(
                                                                    detail?.Status
                                                                        ?.StatusDateTime
                                                                )}
                                                            </p>
                                                        </div>

                                                        {/* Full tracking flow / timeline */}
                                                        <div
                                                            className={`pt-2 border-t ${isDarkMode
                                                                ? 'border-white/10'
                                                                : 'border-black/[0.06]'
                                                                }`}
                                                        >
                                                            <p
                                                                className={`text-[8px] font-bold uppercase tracking-wider mb-2.5 ${isDarkMode
                                                                    ? 'text-white/35'
                                                                    : 'text-black/35'
                                                                    }`}
                                                            >
                                                                Tracking Flow
                                                            </p>

                                                            {scans.length > 0 ? (
                                                                <div>
                                                                    {scans.map((scan, sIndex) => (
                                                                        <div
                                                                            key={sIndex}
                                                                            className="relative pb-4 last:pb-0"
                                                                        >
                                                                            <div className="flex items-start gap-2.5">
                                                                                {/* Step icon */}
                                                                                <div className="relative z-10 flex-shrink-0">
                                                                                    <div
                                                                                        className={`
                                    w-6 h-6 rounded-full
                                    flex items-center justify-center
                                    border
                                    ${sIndex === 0
                                                                                                ? 'bg-black text-white border-black'
                                                                                                : isDarkMode
                                                                                                    ? 'bg-white/10 text-white/50 border-white/10'
                                                                                                    : 'bg-white text-black/35 border-black/10'
                                                                                            }
                                  `}
                                                                                    >
                                                                                        {sIndex === 0 ? (
                                                                                            <FaTruck size={9} />
                                                                                        ) : (
                                                                                            <FaCheckCircle size={9} />
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                                {/* Step content */}
                                                                                <div className="flex-1 pt-0.5">
                                                                                    <p
                                                                                        className={`text-[10px] font-bold leading-relaxed ${isDarkMode
                                                                                            ? 'text-white'
                                                                                            : 'text-black'
                                                                                            }`}
                                                                                    >
                                                                                        {scan?.ScanDetail
                                                                                            ?.Instructions ||
                                                                                            scan?.ScanDetail?.Scan ||
                                                                                            'Shipment update'}
                                                                                    </p>
                                                                                    <p
                                                                                        className={`text-[8px] mt-1 ${isDarkMode
                                                                                            ? 'text-white/35'
                                                                                            : 'text-black/35'
                                                                                            }`}
                                                                                    >
                                                                                        {formatDate(
                                                                                            scan?.ScanDetail
                                                                                                ?.StatusDateTime
                                                                                        )}
                                                                                    </p>
                                                                                    <p
                                                                                        className={`text-[8px] mt-0.5 ${isDarkMode
                                                                                            ? 'text-white/35'
                                                                                            : 'text-black/35'
                                                                                            }`}
                                                                                    >
                                                                                        {scan?.ScanDetail
                                                                                            ?.ScannedLocation ||
                                                                                            'Location unavailable'}
                                                                                    </p>
                                                                                </div>
                                                                            </div>

                                                                            {/* connecting line */}
                                                                            {sIndex < scans.length - 1 && (
                                                                                <div
                                                                                    className={`absolute left-[11px] top-6 w-px h-[calc(100%-14px)] ${isDarkMode
                                                                                        ? 'bg-white/10'
                                                                                        : 'bg-black/10'
                                                                                        }`}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className={`py-4 text-center text-[9.5px] ${isDarkMode
                                                                        ? 'text-white/40'
                                                                        : 'text-black/40'
                                                                        }`}
                                                                >
                                                                    No tracking scans available
                                                                </div>
                                                            )}
                                                        </div>
                                                    </AccordionSection>

                                                    {/* Shipment Overview */}
                                                    <AccordionSection
                                                        icon={FaBox}
                                                        title="Shipment Overview"
                                                        isOpen={openSection === 'overview'}
                                                        onToggle={() =>
                                                            handleToggleSection('overview')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <DetailRow
                                                            label="AWB Number"
                                                            value={detail?.AWB}
                                                            icon={FaBox}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Order ID"
                                                            value={detail?.ReferenceNo}
                                                            icon={FaBox}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Order Type"
                                                            value={detail?.OrderType}
                                                            icon={FaBox}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Invoice Amount"
                                                            value={`₹${detail?.InvoiceAmount || 0}`}
                                                            icon={FaRupeeSign}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="COD Amount"
                                                            value={`₹${detail?.CODAmount || 0}`}
                                                            icon={FaRupeeSign}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Quantity"
                                                            value={detail?.Quantity}
                                                            icon={FaBox}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </AccordionSection>

                                                    {/* Location Details */}
                                                    <AccordionSection
                                                        icon={FaMapMarkerAlt}
                                                        title="Location Details"
                                                        isOpen={openSection === 'location'}
                                                        onToggle={() =>
                                                            handleToggleSection('location')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <DetailRow
                                                            label="Origin"
                                                            value={detail?.Origin}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Destination"
                                                            value={
                                                                detail?.Destination ||
                                                                detail?.Consignee?.City ||
                                                                detail?.Consignee?.State ||
                                                                detail?.ShipmentData?.[0]?.Shipment?.Destination
                                                            }
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Pickup Location"
                                                            value={detail?.PickupLocation}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </AccordionSection>

                                                    {/* Consignee Details */}
                                                    <AccordionSection
                                                        icon={FaUser}
                                                        title="Consignee Details"
                                                        isOpen={openSection === 'consignee'}
                                                        onToggle={() =>
                                                            handleToggleSection('consignee')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <DetailRow
                                                            label="Name"
                                                            value={detail?.Consignee?.Name}
                                                            icon={FaUser}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="City"
                                                            value={
                                                                detail?.Consignee?.City ||
                                                                detail?.Consignee?.State
                                                            }
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Pin Code"
                                                            value={detail?.Consignee?.PinCode}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Country"
                                                            value={detail?.Consignee?.Country}
                                                            icon={FaMapMarkerAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </AccordionSection>

                                                    {/* Important Dates */}
                                                    <AccordionSection
                                                        icon={FaCalendarAlt}
                                                        title="Important Dates"
                                                        isOpen={openSection === 'dates'}
                                                        onToggle={() =>
                                                            handleToggleSection('dates')
                                                        }
                                                        isDarkMode={isDarkMode}
                                                    >
                                                        <DetailRow
                                                            label="Pickup Date"
                                                            value={formatDate(
                                                                detail?.PickUpDate
                                                            )}
                                                            icon={FaCalendarAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="Expected Delivery"
                                                            value={formatDate(
                                                                detail?.ExpectedDeliveryDate
                                                            )}
                                                            icon={FaCalendarAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                        <DetailRow
                                                            label="First Attempt Date"
                                                            value={formatDate(
                                                                detail?.FirstAttemptDate
                                                            )}
                                                            icon={FaCalendarAlt}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </AccordionSection>

                                                    {/* Download POD inside detail section too, if delivered & available */}
                                                    {/* {isDelivered && s?.epod_file && (
                                                        <button
                                                            onClick={() => handleDownloadPOD(s)}
                                                            className={`
                                w-full

                                flex
                                items-center
                                justify-center
                                gap-1.5

                                py-2.5

                                rounded-[13px]

                                text-[10.5px]
                                font-bold

                                border

                                transition-all
                                duration-200

                                ${isDarkMode
                                                                    ? `
                                    bg-blue-500/15
                                    border-blue-400/20
                                    text-blue-300
                                    hover:bg-blue-500/25
                                  `
                                                                    : `
                                    bg-blue-50
                                    border-blue-100
                                    text-blue-600
                                    hover:bg-blue-100
                                  `
                                                                }
                              `}
                                                        >
                                                            <FaFileDownload size={11} />
                                                            Download POD
                                                        </button>
                                                    )} */}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>

                {/* View All */}
                <div
                    className={`
            relative
            z-10

            px-5
            py-3.5

            border-t

            ${isDarkMode ? 'border-white/10' : 'border-black/[0.06]'}
          `}
                >
                    <button
                        onClick={handleViewAll}
                        className="
              w-full

              flex
              items-center
              justify-center
              gap-2

              py-2.5

              rounded-[14px]

              bg-red-600
              text-white

              text-[11px]
              font-bold

              shadow-[0_8px_25px_rgba(0,0,0,0.18)]

              transition-all
              duration-200

              hover:bg-black/85
              hover:-translate-y-[1px]

              active:translate-y-0
            "
                    >
                        <FaListUl size={11} />
                        View All Shipments
                    </button>
                </div>
            </div>

            {/* Status full-text hover tooltip, rendered via portal */}
            <StatusTooltip />
        </>
    );
};

export default TrackingSidePopup;